import React, { useState } from 'react';

import {
  isStCapacityCompany,
  type ServiceTitanAppointment,
  type SetRequiredNonNullable,
  type Technician
} from '_lib/api/apiTypes.shim';
import { WarningAlert } from '_lib/components/Alert/Alert';
import { AlertTitle } from '_lib/components/AlertTitle/AlertTitle';
import { Checkbox } from '_lib/components/Checkbox/Checkbox';
import { DialogActions } from '_lib/components/DialogActions/DialogActions';
import { DialogButton } from '_lib/components/DialogButton/DialogButton';
import { DialogContent } from '_lib/components/DialogContent/DialogContent';
import { Divider } from '_lib/components/Divider/Divider';
import { FieldSet } from '_lib/components/FieldSet/FieldSet';
import { FormControlLabel } from '_lib/components/FormControlLabel/FormControlLabel';
import { ExternalLink } from '_lib/components/Link/Link';
import { Markup } from '_lib/components/Markup/Markup';
import { ShowMoreText } from '_lib/components/ShowMoreText/ShowMoreText';
import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';
import { CompanyLabels } from '_lib/constants/company.constants';
import { DialogWrapper } from '_lib/featureComponents/DialogWrapper/DialogWrapper';
import { HeaderBox } from '_lib/featureComponents/HeaderBox/HeaderBox';
import { TextBlockTypography } from '_lib/featureComponents/TextBoxTypography/TextBoxTypography';
import { AsyncDialogProps, useAsyncDialog } from '_lib/hooks/useAsyncDialog';
import { SharedPhoneCallContext } from '_lib/types/PhoneCall.types';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';
import { getAppointmentNumberAndStatus, getFullName } from '_lib/utils/helpers';

import { CompanyContext, ZoneZipContext } from '../callPop/common/CompanyPane/Company.slice';
import { CompanyScheduleUrlDisplay } from '../callPop/common/CompanyPane/CompanyScheduleUrl/CompanyScheduleUrl';
import { zonedTooltipText } from '../callPop/common/CompanyPane/ZoneOverrideUtils/ZoneOverrideUtils';

import type { OverrideProperties } from 'type-fest';

type CompanyWithAfterHoursReviewEnabled = OverrideProperties<
  SetRequiredNonNullable<CompanyContext['company'], 'contactScheduleUrl'>,
  { isAfterHoursReviewEnabled: true }
>;
type CompanyContextProp = Pick<
  OverrideProperties<CompanyContext, { company: CompanyWithAfterHoursReviewEnabled }>,
  'company'
>;

export const isAfterHoursReviewContext = <T extends Pick<CompanyContextProp, 'company'>>(
  ctx: Pick<CompanyContext, 'company'>
): ctx is T => ctx.company.isAfterHoursReviewEnabled && !!ctx.company.contactScheduleUrl;

type AppointmentAfterHoursDialogProps = AsyncDialogProps & {
  companyContext: CompanyContextProp;
  callOutcome: SharedPhoneCallContext['callOutcome'];
  zoneZipContext?: ZoneZipContext;
  jobNo?: string | null;
  appointmentPartial?: SetRequiredNonNullable<Partial<ServiceTitanAppointment>, 'start'> | null;
  assignedTech?: Technician | null;
};

const AppointmentAfterHoursDialog = (props: AppointmentAfterHoursDialogProps) => {
  const {
    onClose,
    titleText,
    companyContext,
    callOutcome,
    zoneZipContext,
    jobNo,
    appointmentPartial,
    assignedTech,
    ...dialogProps
  } = props;

  const [rulesConfirmed, setRulesConfirmed] = useState(false);

  const { company } = companyContext;

  const contactScheduleUrl =
    company.isZoned && zoneZipContext?.selectedZone?.contactScheduleUrl
      ? zoneZipContext?.selectedZone.contactScheduleUrl
      : company.contactScheduleUrl;

  // if company is zoned, use selectedZone value and do not fallback to Company
  const dispatchRules = company.isZoned
    ? zoneZipContext?.selectedZone?.callCenter.dispatchRules
    : company.callCenter.dispatchRules;
  const onCallNotes = company.isZoned ? zoneZipContext?.selectedZone?.onCallNotes : company.onCallNotes;

  const zonedValueInfoIconText = zonedTooltipText(company);
  return (
    <DialogWrapper
      onClose={onClose}
      titleText={titleText || 'Review Notification and Tech Assign Rules'}
      {...dialogProps}>
      <DialogContent>
        <Stack useFlexGap>
          <WarningAlert>
            <AlertTitle>Appointment is outside of normal business hours</AlertTitle>
            <Typography text={'This appointment may require after hours tech assignment.'} />
          </WarningAlert>
          <FieldSet legendText='Outcome'>
            <Typography text={callOutcome || 'No Outcome Selected'} />
          </FieldSet>
          {appointmentPartial && (
            <FieldSet
              legendText={`Appointment Info ${
                jobNo && appointmentPartial.status
                  ? getAppointmentNumberAndStatus(
                      {
                        appointmentNumber: appointmentPartial.appointmentNumber || null,
                        status: appointmentPartial.status
                      },
                      jobNo
                    )
                  : ''
              }`}>
              <Stack useFlexGap minWidth={0} flexGrow={1}>
                <HeaderBox headerText='Date and Time'>
                  <Typography
                    text={DateUtils.format(appointmentPartial.start, DateFormatEnum.DateTimeWeekday, {
                      timezone: company.timezone,
                      showTimezone: true
                    })}
                  />
                </HeaderBox>
                {appointmentPartial?.specialInstructions && (
                  <HeaderBox headerText='Special Instructions'>
                    <ShowMoreText text={appointmentPartial.specialInstructions} isRichText={true} />
                  </HeaderBox>
                )}
                <HeaderBox headerText='Assigned Technician'>
                  {assignedTech ? (
                    <Typography text={getFullName(assignedTech)} />
                  ) : (
                    <WarningAlert>
                      <Typography text='Review company info to see if Tech Assignment & Notify is necessary.' />
                    </WarningAlert>
                  )}
                </HeaderBox>
              </Stack>
            </FieldSet>
          )}
          <FieldSet
            legendText={`Contact Schedule URL`}
            infoIconText={zoneZipContext?.selectedZone?.contactScheduleUrl ? zonedValueInfoIconText : undefined}>
            <ExternalLink href={contactScheduleUrl}>{CompanyLabels.CONTACT_SCHEDULE_URL}</ExternalLink>
          </FieldSet>
          {!company.isScheduleEnabled && !isStCapacityCompany(company) && (
            <FieldSet legendText={`Schedule URL`} infoIconText={zonedValueInfoIconText}>
              <CompanyScheduleUrlDisplay
                company={company}
                overrideZipMatch={zoneZipContext?.overrideZipMatch}
                selectedZipOrPostal={zoneZipContext?.selectedZipOrPostal}
                selectedZone={zoneZipContext?.selectedZone}
              />
            </FieldSet>
          )}
          <FieldSet legendText={`Dispatch Rules`} infoIconText={zonedValueInfoIconText}>
            <TextBlockTypography text={dispatchRules} />
          </FieldSet>
          <FieldSet legendText={`On Call Instructions`} infoIconText={zonedValueInfoIconText}>
            <Markup
              typographyProps={{ sx: { whiteSpace: 'pre-line', overflowWrap: 'break-word' } }}
              content={onCallNotes || 'N/A'}
            />
          </FieldSet>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <FormControlLabel
          sx={{ paddingLeft: 1 }}
          control={
            <Checkbox
              color='primary'
              checked={rulesConfirmed}
              onChange={(e) => setRulesConfirmed(!!e.target.checked)}
            />
          }
          label={<Typography text='I have followed the tech assign and notify rules' />}
        />
        <Stack useFlexGap direction='row'>
          <DialogButton color='cancel' onClick={() => onClose({ action: 'CANCEL' })} text='Cancel' />
          <DialogButton disabled={!rulesConfirmed} onClick={() => onClose({ action: 'CONFIRM' })} text='Confirm' />
        </Stack>
      </DialogActions>
    </DialogWrapper>
  );
};

export const useAppointmentAfterHoursDialog = () => useAsyncDialog(AppointmentAfterHoursDialog);
